"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogHeader,
   DialogTitle,
} from "@/components/ui/dialog";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import {useGailGptMemories} from "@/hooks/useGailGptMemories";
import {MAX_MEMORY_CHARS, type Mem0PublicItem} from "@/services/mem0/types";
import MemoryImportPanel from "./MemoryImportPanel";
import MemoryKnowledgeBaseLink from "./MemoryKnowledgeBaseLink";
import MemoryListItem from "./MemoryListItem";

type GailGptMemoryDialogProps = Readonly<{
   open: boolean;
   onOpenChange: (open: boolean) => void;
}>;

function MemoryDialogBody({
   loading,
   error,
   items,
   mutating,
   onSave,
   onDelete,
   loadingLabel,
   emptyLabel,
}: Readonly<{
   loading: boolean;
   error: string | null;
   items: Mem0PublicItem[];
   mutating: boolean;
   onSave: (id: string, text: string) => Promise<boolean>;
   onDelete: (id: string) => Promise<boolean>;
   loadingLabel: string;
   emptyLabel: string;
}>) {
   if (loading) {
      return (
         <div className="text-500 flex items-center gap-2 py-6 text-sm">
            <Icon icon="spinner" type={ICON_TYPE.LIGHT} className="animate-spin" />
            {loadingLabel}
         </div>
      );
   }
   if (!error && items.length === 0) {
      return <p className="text-500 py-6 text-sm">{emptyLabel}</p>;
   }
   if (items.length === 0) {
      return null;
   }
   return (
      <ul className="flex flex-col gap-2">
         {items.map((item) => (
            <MemoryListItem
               key={item.id}
               item={item}
               disabled={mutating || loading}
               onSave={onSave}
               onDelete={onDelete}
            />
         ))}
      </ul>
   );
}

export default function GailGptMemoryDialog({open, onOpenChange}: GailGptMemoryDialogProps) {
   const {t} = useTranslation("ask");
   const {items, loading, error, mutating, importing, add, update, remove, importMemories} =
      useGailGptMemories(open);
   const [draft, setDraft] = useState("");
   const [composer, setComposer] = useState<"none" | "single" | "import">("none");

   const handleAdd = async (event: {preventDefault: () => void}) => {
      event.preventDefault();
      const text = draft.trim();
      if (!text) return;
      const ok = await add(text);
      if (ok) {
         setDraft("");
         setComposer("none");
      }
   };

   const handleOpenChange = (next: boolean) => {
      // An import keeps running server-side; closing now would discard the
      // panel's result state and invite a duplicate retry.
      if (!next && importing) return;
      onOpenChange(next);
   };

   useEffect(() => {
      if (!open) {
         setComposer("none");
         setDraft("");
      }
   }, [open]);

   return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
         <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden">
            <DialogHeader>
               <DialogTitle>{t("memory.title")}</DialogTitle>
               <DialogDescription>
                  {t("memory.description")}{" "}
                  <MemoryKnowledgeBaseLink />
               </DialogDescription>
            </DialogHeader>

            {error && (
               <p className="text-red-600 dark:text-red-400 text-sm" role="alert">
                  {t(error)}
               </p>
            )}

            {composer === "none" && (
               <>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                     <MemoryDialogBody
                        loading={loading}
                        error={error}
                        items={items}
                        mutating={mutating || importing}
                        onSave={update}
                        onDelete={remove}
                        loadingLabel={t("memory.loading")}
                        emptyLabel={t("memory.empty")}
                     />
                  </div>

                  <div className="flex gap-2">
                     <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5"
                        onClick={() => setComposer("single")}
                     >
                        <Icon icon="plus" type={ICON_TYPE.LIGHT} />
                        {t("memory.addMemory")}
                     </Button>
                     <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5"
                        onClick={() => setComposer("import")}
                     >
                        <Icon icon="sparkles" type={ICON_TYPE.LIGHT} />
                        {t("memory.importMemories")}
                     </Button>
                  </div>
               </>
            )}

            {composer === "single" && (
               <form
                  className="animate-fade-in flex flex-col gap-2"
                  onSubmit={(event) => void handleAdd(event)}
               >
                  <textarea
                     value={draft}
                     onChange={(event) => setDraft(event.target.value)}
                     placeholder={t("memory.addPlaceholder")}
                     aria-label={t("memory.addAriaLabel")}
                     disabled={mutating || loading}
                     rows={2}
                     maxLength={MAX_MEMORY_CHARS}
                     className="border-input bg-transparent w-full rounded-md border px-3 py-2 text-sm"
                  />
                  <div className="flex justify-end gap-2">
                     <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={mutating}
                        onClick={() => {
                           setDraft("");
                           setComposer("none");
                        }}
                     >
                        {t("memory.cancel")}
                     </Button>
                     <Button
                        type="submit"
                        size="sm"
                        disabled={mutating || loading || !draft.trim()}
                     >
                        {mutating && (
                           <Icon icon="spinner" type={ICON_TYPE.LIGHT} className="animate-spin" />
                        )}
                        {t("memory.add")}
                     </Button>
                  </div>
               </form>
            )}

            {composer === "import" && (
               <MemoryImportPanel
                  importing={importing}
                  disabled={loading}
                  onImport={importMemories}
                  onCancel={() => setComposer("none")}
               />
            )}
         </DialogContent>
      </Dialog>
   );
}
